import { useState } from 'react';
import { X, CheckCircle, AlertTriangle, Copy, Check, Mail, Link as LinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface InviteStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailSent: boolean;
  email: string;
  inviteUrl: string;
  emailError?: {
    message: string;
    status?: number;
    name?: string;
  } | null;
}

export function InviteStatusModal({
  isOpen,
  onClose,
  emailSent,
  email,
  inviteUrl,
  emailError
}: InviteStatusModalProps) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      log.error('Failed to copy:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              {emailSent ? (
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              ) : (
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {emailSent ? t('admin.invitationSent') : t('admin.invitationCreated')}
                </h2>
                <p className="text-gray-600 mt-1">
                  {email}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {emailSent ? (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900">
                    {t('admin.emailSentSuccessfully')}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {t('admin.emailSentMessage')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">
                    {t('admin.emailNotSent')}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {t('admin.emailNotSentMessage')}
                  </p>
                  {emailError && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-amber-800 hover:text-amber-900">
                        {t('admin.technicalDetails')}
                      </summary>
                      <div className="mt-2 p-3 bg-white rounded border border-amber-200">
                        <p className="text-xs font-mono text-gray-700">
                          <strong>Error:</strong> {emailError.message}
                        </p>
                        {emailError.status && (
                          <p className="text-xs font-mono text-gray-700 mt-1">
                            <strong>Status:</strong> {emailError.status}
                          </p>
                        )}
                        {emailError.name && (
                          <p className="text-xs font-mono text-gray-700 mt-1">
                            <strong>Type:</strong> {emailError.name}
                          </p>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                {t('admin.invitationLink')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {emailSent
                ? t('admin.invitationLinkBackup')
                : t('admin.invitationLinkShare')}
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={inviteUrl}
                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-4 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors font-medium"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    {t('admin.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    {t('admin.copy')}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">
              {t('admin.nextSteps')}
            </h4>
            <ul className="space-y-2 text-sm text-blue-800">
              {!emailSent && (
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-0.5">1.</span>
                  <span>{t('admin.nextStepShare')}</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="font-bold mt-0.5">{emailSent ? '1' : '2'}.</span>
                <span>{t('admin.nextStepUser')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold mt-0.5">{emailSent ? '2' : '3'}.</span>
                <span>{t('admin.nextStepExpiry')}</span>
              </li>
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
